import {
  School, CalendarDays, CreditCard, GraduationCap, Users, UserPlus,
  Trophy, Sparkles, FileText, Megaphone, Printer, Rocket, BellRing, BadgeCheck,
} from "lucide-react";

// Copy for the setup guide. Status, order, tiers and dependencies come
// from the server (Onboarding::SetupGuide); this file says what each
// step is and why it matters, in the voice of someone walking a school
// through it in person.

export const PHASES = [
  { key: "foundations", title: "Foundations", description: "The when, the why, and where the money goes." },
  { key: "people", title: "People", description: "Your students, your teachers, and anyone helping you run it." },
  { key: "rewards", title: "Rewards", description: "Something for every reader to work toward." },
  { key: "spread_the_word", title: "Spread the word", description: "Teachers first, then families." },
  { key: "launch", title: "Launch", description: "Go live on your start date." },
];

export const STEP_COPY = {
  school_details: {
    title: "Check your school details",
    short: "School details",
    icon: School,
    effort: "1 minute",
    why: "Your school's name goes on every donation page, receipt and sheet that goes home. The timezone decides when each campaign day starts and ends, so it's worth a quick look before you pick dates.",
    help: "school-settings",
  },
  campaign: {
    title: "Plan your read-a-thon",
    short: "Dates & goal",
    icon: CalendarDays,
    effort: "5 minutes",
    why: "Pick your dates, say what you're raising money for, and set a goal. The info sheets, countdowns and reminders all build on these.",
    help: "creating-a-campaign",
  },
  payment: {
    title: "Pay the campaign fee",
    short: "Payment",
    icon: BadgeCheck,
    effort: "5 minutes, plus the e-Transfer",
    why: "Setting up is free. Running the campaign costs a flat fee, based on how many students take part. Once it's paid, we approve the campaign, and Square, teachers and launch open up.",
    help: "paying-for-your-campaign",
  },
  square: {
    title: "Connect Square",
    short: "Square",
    icon: CreditCard,
    effort: "5 minutes, plus Square's bank check",
    why: "Donations go straight from donors to your school's Square account. PagePledge never holds the money. Start early: a new Square account has to have its bank account verified before card payments go through.",
    help: "connecting-square",
  },
  roster: {
    title: "Add your students",
    short: "Students",
    icon: GraduationCap,
    effort: "About 10 minutes",
    why: "Every student gets a reading page and a take-home sheet with an activation code for their family. Upload a class list and PagePledge creates the classrooms for you.",
    help: "importing-roster-csv",
  },
  teachers: {
    title: "Invite your teachers",
    short: "Teachers",
    icon: Users,
    effort: "About 5 minutes",
    why: "Teachers log class reading time, print their own class's sheets and hand out prizes. Each one gets a single welcome email with a sign-in link. There are no passwords.",
    help: "inviting-teachers",
  },
  team: {
    title: "Add a co-organizer",
    short: "Co-organizers",
    icon: UserPlus,
    effort: "1 minute",
    why: "A read-a-thon is easier with a second pair of hands: a parent council member, your treasurer, or a lead teacher. Co-organizers are school admins and can do everything you can.",
    help: "managing-members",
  },
  school_rewards: {
    title: "Set school-wide rewards",
    short: "School rewards",
    icon: Trophy,
    effort: "About 5 minutes",
    why: "Rewards the whole school earns together as it reaches fundraising or reading amounts. They're printed on the info and take-home sheets, so set them before those go home.",
    help: "setting-school-milestones",
  },
  student_prizes: {
    title: "Add prizes for individual readers",
    short: "Student prizes",
    icon: Sparkles,
    effort: "About 5 minutes",
    why: "Prizes each student earns on their own, like a bookmark for reading 240 minutes. You can also reward the top reader in every class, or run a prize draw.",
    help: "setting-student-prizes",
  },
  teacher_info: {
    title: "Brief your teachers",
    short: "Teacher info",
    icon: FileText,
    effort: "2 minutes",
    why: "A plain-language overview of the read-a-thon and exactly what you need from teachers, with your dates and prizes. It's already written. Read it over, then download it to email or print.",
    help: "getting-started-school-admin",
  },
  family_info: {
    title: "Tell families what's coming",
    short: "Family info",
    icon: Megaphone,
    effort: "5 minutes, plus printing",
    why: "One page for every family: what a read-a-thon is, what happens next, your key dates and prizes. It goes home the week before the take-home sheets, so they're expected when they arrive.",
    help: "getting-started-school-admin",
  },
  take_home_sheets: {
    title: "Print take-home sheets",
    short: "Take-home sheets",
    icon: Printer,
    effort: "10 minutes, plus printing",
    why: "One sheet per student, with a QR code their family scans to activate their reading page. This is how families join, so it's the sheet that matters most.",
    help: "printing-take-home-sheets",
  },
  notifications: {
    title: "Choose who gets which emails",
    short: "Notifications",
    icon: BellRing,
    effort: "2 minutes",
    why: "PagePledge emails admins, teachers and families at the key moments: when reading starts, with daily or weekly progress, as it wraps up, and with the final results. They're all on to start with. Switch off any your school would rather send itself.",
    help: "campaign-lifecycle",
  },
  launch: {
    title: "Launch",
    short: "Launch",
    icon: Rocket,
    why: "Your campaign goes live by itself early on the start date. Here's what's ready, what's left, and what happens next.",
    help: "campaign-lifecycle",
  },
};

export const TIERS = {
  required: { label: "Required", description: "Needed to launch" },
  recommended: { label: "Recommended", description: "Skip it only if you have your own plan" },
  optional: { label: "Optional", description: "A nice extra" },
};

// Why a step is best done after another one ("best after" dependencies).
export const WAITING_ON_REASONS = {
  teacher_info: {
    teachers: "Invite your teachers first, so they can sign in as soon as they've read it.",
    school_rewards: "Set your school-wide rewards first so the sheet lists them.",
  },
  family_info: {
    school_rewards: "Set your school-wide rewards first so the sheet lists them.",
  },
  take_home_sheets: {
    payment: "Families can't activate their child's page until your campaign is approved, so send the sheets home once it's paid for.",
  },
};

// Steps waiting on an unpaid campaign fee (step.locked, set by the
// server) show a padlock, not just their turn in the order
// (docs/BUSINESS_RULES.md §2a). Other blocked steps keep their number.
export function displayStatus(step) {
  return step.locked ? "locked" : step.status;
}

export const LOCKED_LABEL = "Locked until paid";

// The badge beside an unpaid campaign: a new school's free trial, or a
// returning customer's next campaign.
export function trialLabel(trial) {
  return trial.school_paid ? "Awaiting payment" : "Free trial";
}

// “A”, “A and B”, “A, B and C”
export function listJoin(items) {
  return items.length > 1 ? `${items.slice(0, -1).join(", ")} and ${items.at(-1)}` : items[0] || "";
}

export function helpHref(slug) {
  return `/help/articles/${slug}`;
}
